import { queryKeys, useHttpClient, useSession } from '@repo/api-client/react';
import {
  OrganizationSchema,
  type Organization,
  type OrganizationListItem,
} from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteJSON } from '../internal/http';
import { syncOrgsFromCache } from './sessionSync';

/**
 * Archive an organization (soft-delete).
 *
 * Mirrors `DELETE /v1/orgs/:orgId` which on the server stamps
 * `archivedAt` and returns the now-archived row. We keep the row in
 * the detail cache (callers may still want to render it briefly while
 * navigating away) but drop it from the list cache so switchers and
 * settings stop showing the archived workspace.
 *
 * The active-organization id is *not* cleared here — that's the
 * caller's job (see `useSetActiveOrganization`) because the screen
 * that triggered the archive may want to choose a sensible fallback
 * (e.g. the next remaining org) before redirecting.
 */
export function useArchiveOrganization() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const { syncOrganizations } = useSession();

  const mutation = useMutation({
    mutationFn: (organizationId: string) =>
      deleteJSON(http, `/orgs/${organizationId}`, OrganizationSchema),
    onSuccess: (organization: Organization) => {
      // Update the detail row so any screen still subscribed sees the
      // freshly stamped `archivedAt`.
      queryClient.setQueryData(
        queryKeys.organizations.detail(organization.id),
        organization,
      );
      // Drop it from the list cache so switchers stop showing it.
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.all(),
        (cache) =>
          cache
            ? { ...cache, items: cache.items.filter((o) => o.id !== organization.id) }
            : cache,
      );
      // Keep the session projection in lockstep so the active-org
      // guard sees the same list the rest of the app does.
      syncOrgsFromCache(queryClient, syncOrganizations);
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all() });
    },
  });

  return {
    archiveOrganization: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
